import * as React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { Trash2Icon } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { ReferenceCandidates } from 'components/ReferenceCandidates/ReferenceCandidates';
import { useReferenceCandidates } from 'components/ReferenceCandidates/useReferenceCandidates';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Input } from 'components/input/Input';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { getQuickQuoteSuggestion } from 'lib/api/getQuickQuoteSuggestion';
import { NormalizedLoad, TMSEquipmentType } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';
import QuoteNumberSelect, {
  QuoteNumberOption,
  mapSuggestedQuoteToOption,
} from './QuoteNumberSelect';
import SpecialReferenceSelect from './SpecialReferenceSelect';
import {
  MGB_CARRIER_REP_REF_TYPE,
  MGB_CUSTOMER_REP_REF_TYPE,
  MGB_QUOTE_NUMBER_REF_TYPE,
  MGB_RATE_MAX_REF_TYPE,
  MGB_RATE_MIN_REF_TYPE,
  MGB_TRAILER_LENGTH_REF_TYPE,
} from './constants';
import {
  emptyAdditionalReference,
  useMGBReferenceCandidateAdapter,
} from './useMGBReferenceCandidateAdapter';
import {
  applyQuoteNumberSelectionToRefs,
  formatDrumkitQuoteNumber,
  getAdditionalReferenceNumber,
  getSpecialReferenceNumber,
  isSystemManagedRef,
  resolveSpecialReferenceCode,
  setAdditionalReferenceNumber,
  setSpecialReferenceNumber,
  shouldApplySuggestionQuoteAutofill,
  stripEmptySystemManagedRefs,
} from './utils';

const EMPTY_ADDITIONAL_REFERENCES: NonNullable<
  NormalizedLoad['additionalReferences']
> = [];

function specialRefsWireSignature(
  refs: NormalizedLoad['additionalReferences'] | undefined
): string {
  return (refs ?? [])
    .map(
      (ref) =>
        `${(ref.qualifier ?? '').trim().toLowerCase()}\0${(ref.number ?? '').trim()}`
    )
    .join('\n');
}

type AdditionalReferencesFormProps = {
  referenceTypes: MaybeUndef<TMSEquipmentType[]>;
  isLoading: boolean;
  isCreateMode: boolean;
  customerSelected?: boolean;
  customerRepOptions?: MaybeUndef<TMSEquipmentType[]>;
  isCustomerRepsLoading?: boolean;
  carrierRepOptions?: MaybeUndef<TMSEquipmentType[]>;
  isCarrierRepsLoading?: boolean;
  trailerLengthOptions?: MaybeUndef<TMSEquipmentType[]>;
  isTrailerLengthsLoading?: boolean;
  /** Bumped by the parent on a real customer switch (never on reset). */
  customerRepClearToken?: number;
};

export default function AdditionalReferencesForm({
  referenceTypes,
  isLoading,
  isCreateMode = false,
  customerSelected = false,
  customerRepOptions,
  isCustomerRepsLoading = false,
  carrierRepOptions,
  isCarrierRepsLoading = false,
  trailerLengthOptions,
  isTrailerLengthsLoading = false,
  customerRepClearToken = 0,
}: AdditionalReferencesFormProps): React.JSX.Element {
  const posthog = usePostHog();
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<NormalizedLoad>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'additionalReferences',
  });
  const additionalReferences =
    useWatch({ control, name: 'additionalReferences' }) ??
    EMPTY_ADDITIONAL_REFERENCES;
  const customerRepClearTokenRef = useRef(customerRepClearToken);
  // MG-generated LD # (customer_ref_number); Overview labels this BOL.
  // Shown read-only in view/edit only (create has no BOL yet).
  const bolNumber =
    useWatch({ control, name: 'customer.refNumber' })?.trim() ?? '';
  const showBol = !isCreateMode && Boolean(bolNumber);

  const {
    currentState: { threadId, clickedSuggestion, latestQuoteRequestId },
  } = useContext(SidebarStateContext);

  const [quoteOptions, setQuoteOptions] = useState<QuoteNumberOption[]>([]);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [userOverrodeQuote, setUserOverrodeQuote] = useState(false);
  const [userOverrodeRateBand, setUserOverrodeRateBand] = useState(false);
  const lastLoadBuildingSuggestionIdRef = useRef<number | null>(null);
  const lastLatestQuoteRequestIdRef = useRef<number | null>(null);
  const quotesFetchedForThreadRef = useRef<Maybe<string>>(null);
  const quoteScopedThreadIdRef = useRef<Maybe<string>>(null);

  const quoteNumber = getAdditionalReferenceNumber(
    additionalReferences,
    MGB_QUOTE_NUMBER_REF_TYPE
  );
  const rateMin = getAdditionalReferenceNumber(
    additionalReferences,
    MGB_RATE_MIN_REF_TYPE
  );
  const rateMax = getAdditionalReferenceNumber(
    additionalReferences,
    MGB_RATE_MAX_REF_TYPE
  );
  const customerRep = getSpecialReferenceNumber(
    additionalReferences,
    MGB_CUSTOMER_REP_REF_TYPE
  );
  const carrierRep = getSpecialReferenceNumber(
    additionalReferences,
    MGB_CARRIER_REP_REF_TYPE
  );
  const trailerLength = getSpecialReferenceNumber(
    additionalReferences,
    MGB_TRAILER_LENGTH_REF_TYPE
  );

  const suggestionDkqn = useMemo(() => {
    if (!isCreateMode) {
      return '';
    }
    if (clickedSuggestion?.pipeline !== SuggestionPipelines.LoadBuilding) {
      return '';
    }
    const lb = clickedSuggestion as LoadBuildingSuggestions;
    return (
      lb.drumkitQuoteNumber?.trim() ||
      (lb.quoteRequestId && lb.quoteRequestId > 0
        ? formatDrumkitQuoteNumber(lb.quoteRequestId)
        : '')
    );
  }, [isCreateMode, clickedSuggestion]);

  const threadLatestDkqn = useMemo(() => {
    if (!isCreateMode || latestQuoteRequestId <= 0) {
      return '';
    }
    return formatDrumkitQuoteNumber(latestQuoteRequestId);
  }, [isCreateMode, latestQuoteRequestId]);

  const autofillDkqn = suggestionDkqn || threadLatestDkqn;

  const showQuoteNumberAiHint =
    Boolean(autofillDkqn) && quoteNumber === autofillDkqn && !userOverrodeQuote;

  useEffect(() => {
    const stripped = stripEmptySystemManagedRefs(additionalReferences);
    if (stripped.length !== additionalReferences.length) {
      replace(stripped);
    }
  }, [additionalReferences, replace]);

  // Load thread QQ searches for the Quote Number dropdown (create mode).
  // On thread change (or leaving create/thread), drop form Quote Number + Rate
  // Min/Max so a prior email's DKQN cannot remain selected or associate create.
  useEffect(() => {
    const clearThreadScopedQuoteRefs = () => {
      replace(
        applyQuoteNumberSelectionToRefs(getValues('additionalReferences'), {
          drumkitQuoteNumber: '',
        })
      );
      setQuoteOptions([]);
      setUserOverrodeQuote(false);
      setUserOverrodeRateBand(false);
      lastLoadBuildingSuggestionIdRef.current = null;
      lastLatestQuoteRequestIdRef.current = null;
      quotesFetchedForThreadRef.current = null;
      quoteScopedThreadIdRef.current = null;
    };

    if (!isCreateMode || !threadId) {
      setQuoteOptions([]);
      setIsLoadingQuotes(false);
      if (isCreateMode) {
        clearThreadScopedQuoteRefs();
      }
      return;
    }

    if (quoteScopedThreadIdRef.current !== threadId) {
      clearThreadScopedQuoteRefs();
      quoteScopedThreadIdRef.current = threadId;
    }

    let cancelled = false;
    setIsLoadingQuotes(true);
    void (async () => {
      const res = await getQuickQuoteSuggestion(threadId);
      if (cancelled) {
        return;
      }
      if (res.isOk()) {
        setQuoteOptions(res.value.map(mapSuggestedQuoteToOption));
      } else {
        setQuoteOptions([]);
      }
      quotesFetchedForThreadRef.current = threadId;
      setIsLoadingQuotes(false);
    })();

    return () => {
      cancelled = true;
      setIsLoadingQuotes(false);
    };
  }, [isCreateMode, threadId, replace, getValues]);

  // Autofill from load-building suggestion best-guess (ENG-7570).
  // DKQN can seed from the suggestion before thread QQ options finish loading;
  // Rate Min/Max backfill when the matching option arrives with a buy band.
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    const lb =
      clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
        ? (clickedSuggestion as LoadBuildingSuggestions)
        : null;

    if (!lb?.id) {
      if (lastLoadBuildingSuggestionIdRef.current != null) {
        lastLoadBuildingSuggestionIdRef.current = null;
        setUserOverrodeQuote(false);
        setUserOverrodeRateBand(false);
      }
      return;
    }

    const dkqn =
      lb.drumkitQuoteNumber?.trim() ||
      (lb.quoteRequestId && lb.quoteRequestId > 0
        ? formatDrumkitQuoteNumber(lb.quoteRequestId)
        : '');

    if (lastLoadBuildingSuggestionIdRef.current !== lb.id) {
      lastLoadBuildingSuggestionIdRef.current = lb.id;
      setUserOverrodeQuote(false);
      setUserOverrodeRateBand(false);

      if (!dkqn) {
        replace(
          applyQuoteNumberSelectionToRefs(getValues('additionalReferences'), {
            drumkitQuoteNumber: '',
          })
        );
        return;
      }
    }

    if (!dkqn) {
      return;
    }

    const currentQuote = getAdditionalReferenceNumber(
      additionalReferences,
      MGB_QUOTE_NUMBER_REF_TYPE
    );
    const optionOnThread = quoteOptions.find(
      (o) =>
        o.id === lb.quoteRequestId ||
        o.drumkitQuoteNumber === dkqn ||
        (lb.quoteRequestId != null &&
          o.drumkitQuoteNumber === formatDrumkitQuoteNumber(lb.quoteRequestId))
    );
    const lowBuyRate = lb.lowBuyRate ?? optionOnThread?.lowBuyRate;
    const highBuyRate = lb.highBuyRate ?? optionOnThread?.highBuyRate;
    const hasBuyBand =
      (lowBuyRate != null && Number.isFinite(lowBuyRate) && lowBuyRate > 0) ||
      (highBuyRate != null && Number.isFinite(highBuyRate) && highBuyRate > 0);
    const formMissingRateBand = !rateMin && !rateMax;

    if (
      shouldApplySuggestionQuoteAutofill({
        expectedDkqn: dkqn,
        currentQuoteOnForm: currentQuote,
        userOverrodeQuote,
      })
    ) {
      const next = applyQuoteNumberSelectionToRefs(additionalReferences, {
        drumkitQuoteNumber: dkqn,
        lowBuyRate,
        highBuyRate,
        syncRateBand: hasBuyBand,
      });
      replace(next);
      posthog?.capture(ButtonNamePosthog.MGBQuoteNumberAutofilled, {
        quoteRequestId: lb.quoteRequestId,
        drumkitQuoteNumber: dkqn,
      });
      return;
    }

    if (
      !userOverrodeQuote &&
      !userOverrodeRateBand &&
      currentQuote === dkqn &&
      formMissingRateBand &&
      hasBuyBand &&
      !isLoadingQuotes
    ) {
      const next = applyQuoteNumberSelectionToRefs(additionalReferences, {
        drumkitQuoteNumber: dkqn,
        lowBuyRate,
        highBuyRate,
        syncRateBand: true,
      });
      replace(next);
    }
  }, [
    isCreateMode,
    threadId,
    isLoadingQuotes,
    clickedSuggestion,
    quoteOptions,
    additionalReferences,
    rateMin,
    rateMax,
    userOverrodeQuote,
    userOverrodeRateBand,
    replace,
    getValues,
    posthog,
  ]);

  // Autofill from thread's latest Quick Quote when no load-building DKQN (ENG-7582).
  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (suggestionDkqn) {
      return;
    }

    if (!latestQuoteRequestId || latestQuoteRequestId <= 0) {
      if (lastLatestQuoteRequestIdRef.current != null) {
        lastLatestQuoteRequestIdRef.current = null;
      }
      return;
    }

    const dkqn = formatDrumkitQuoteNumber(latestQuoteRequestId);
    if (!dkqn) {
      return;
    }

    if (lastLatestQuoteRequestIdRef.current !== latestQuoteRequestId) {
      lastLatestQuoteRequestIdRef.current = latestQuoteRequestId;
      setUserOverrodeQuote(false);
      setUserOverrodeRateBand(false);
    }

    const currentQuote = getAdditionalReferenceNumber(
      additionalReferences,
      MGB_QUOTE_NUMBER_REF_TYPE
    );
    const optionOnThread = quoteOptions.find(
      (o) =>
        o.id === latestQuoteRequestId ||
        o.drumkitQuoteNumber === dkqn ||
        o.drumkitQuoteNumber === formatDrumkitQuoteNumber(latestQuoteRequestId)
    );
    const lowBuyRate = optionOnThread?.lowBuyRate;
    const highBuyRate = optionOnThread?.highBuyRate;
    const hasBuyBand =
      (lowBuyRate != null && Number.isFinite(lowBuyRate) && lowBuyRate > 0) ||
      (highBuyRate != null && Number.isFinite(highBuyRate) && highBuyRate > 0);
    const formMissingRateBand = !rateMin && !rateMax;

    if (
      shouldApplySuggestionQuoteAutofill({
        expectedDkqn: dkqn,
        currentQuoteOnForm: currentQuote,
        userOverrodeQuote,
      })
    ) {
      const next = applyQuoteNumberSelectionToRefs(additionalReferences, {
        drumkitQuoteNumber: dkqn,
        lowBuyRate,
        highBuyRate,
        syncRateBand: hasBuyBand,
      });
      replace(next);
      posthog?.capture(ButtonNamePosthog.MGBQuoteNumberAutofilled, {
        quoteRequestId: latestQuoteRequestId,
        drumkitQuoteNumber: dkqn,
      });
      return;
    }

    if (
      !userOverrodeQuote &&
      !userOverrodeRateBand &&
      currentQuote === dkqn &&
      formMissingRateBand &&
      hasBuyBand &&
      !isLoadingQuotes
    ) {
      const next = applyQuoteNumberSelectionToRefs(additionalReferences, {
        drumkitQuoteNumber: dkqn,
        lowBuyRate,
        highBuyRate,
        syncRateBand: true,
      });
      replace(next);
    }
  }, [
    isCreateMode,
    suggestionDkqn,
    latestQuoteRequestId,
    isLoadingQuotes,
    quoteOptions,
    additionalReferences,
    rateMin,
    rateMax,
    userOverrodeQuote,
    userOverrodeRateBand,
    replace,
    getValues,
    posthog,
  ]);

  useEffect(() => {
    const specialFields: Array<{
      qualifier: string;
      options: MaybeUndef<TMSEquipmentType[]>;
    }> = [
      { qualifier: MGB_CUSTOMER_REP_REF_TYPE, options: customerRepOptions },
      { qualifier: MGB_CARRIER_REP_REF_TYPE, options: carrierRepOptions },
      { qualifier: MGB_TRAILER_LENGTH_REF_TYPE, options: trailerLengthOptions },
    ];

    let next = additionalReferences;
    let changed = false;
    for (const field of specialFields) {
      const stored = getSpecialReferenceNumber(next, field.qualifier);
      if (!stored) {
        continue;
      }
      const code = field.options?.length
        ? resolveSpecialReferenceCode(stored, field.options)
        : stored;
      const canonicalized = setSpecialReferenceNumber(
        next,
        field.qualifier,
        code
      );
      if (
        specialRefsWireSignature(canonicalized) !==
        specialRefsWireSignature(next)
      ) {
        next = canonicalized;
        changed = true;
      }
    }
    if (changed) {
      replace(next);
    }
  }, [
    additionalReferences,
    customerRepOptions,
    carrierRepOptions,
    trailerLengthOptions,
    replace,
  ]);

  useEffect(() => {
    if (customerRepClearTokenRef.current === customerRepClearToken) {
      return;
    }
    customerRepClearTokenRef.current = customerRepClearToken;

    const refs = getValues('additionalReferences');
    const next = setSpecialReferenceNumber(refs, MGB_CUSTOMER_REP_REF_TYPE, '');
    if (specialRefsWireSignature(next) !== specialRefsWireSignature(refs)) {
      replace(next);
    }
  }, [customerRepClearToken, getValues, replace]);

  const selectableReferenceTypes = useMemo(
    () =>
      (referenceTypes ?? []).filter(
        (item) =>
          Boolean(item.id) &&
          !isSystemManagedRef(item.id) &&
          !isSystemManagedRef(item.name)
      ),
    [referenceTypes]
  );

  const selectableReferenceTypeKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of selectableReferenceTypes) {
      const id = item.id?.trim().toLowerCase();
      const name = item.name?.trim().toLowerCase();
      if (id) keys.add(id);
      if (name) keys.add(name);
    }
    return keys;
  }, [selectableReferenceTypes]);

  const referenceCandidates = useReferenceCandidates();
  const referenceCandidateAdapter = useMGBReferenceCandidateAdapter({
    referenceTypes: selectableReferenceTypes,
    additionalReferences,
    appendReference: append,
  });

  const editableIndexes = fields
    .map((_, index) => index)
    .filter((index) => {
      const qualifier = additionalReferences[index]?.qualifier?.trim() ?? '';
      if (isSystemManagedRef(qualifier)) {
        return false;
      }
      if (!qualifier) {
        return true;
      }
      if (selectableReferenceTypeKeys.size === 0) {
        return true;
      }
      return selectableReferenceTypeKeys.has(qualifier.toLowerCase());
    });

  const upsertRateRef = (qualifier: string, number: string) => {
    const next = setAdditionalReferenceNumber(
      additionalReferences,
      qualifier,
      number
    );
    replace(next);
    // Stop delayed buy-band backfill from rewriting a deliberate empty/edited rate.
    setUserOverrodeRateBand(true);
  };

  const upsertSpecialReference = (qualifier: string, code: string) => {
    replace(setSpecialReferenceNumber(additionalReferences, qualifier, code));
  };

  const handleQuoteNumberChange = ({
    drumkitQuoteNumber,
    option,
  }: {
    drumkitQuoteNumber: string;
    option?: QuoteNumberOption;
  }) => {
    const cleared = !drumkitQuoteNumber.trim();
    const next = applyQuoteNumberSelectionToRefs(additionalReferences, {
      drumkitQuoteNumber,
      lowBuyRate: option?.lowBuyRate,
      highBuyRate: option?.highBuyRate,
      syncRateBand: Boolean(option) && !cleared,
    });
    replace(next);
    setUserOverrodeQuote(true);
    setUserOverrodeRateBand(false);
    if (!cleared) {
      posthog?.capture(ButtonNamePosthog.MGBQuoteNumberChanged, {
        drumkitQuoteNumber,
        quoteRequestId: option?.id,
      });
    }
  };

  return (
    <Grid cols='4' gap='lg' className='mx-0 w-full'>
      {!isCreateMode && (
        <div className='col-span-4'>
          <LoadBuildingTextInput
            name='drumkitUUID'
            label='Drumkit UUID'
            readOnly
          />
        </div>
      )}

      {isCreateMode ? (
        <>
          <div className='col-span-4'>
            <Label
              name='quoteNumber'
              hideAIHint
              className='text-sm text-neutral-800'
            >
              Drumkit Quote Number
            </Label>
            <QuoteNumberSelect
              value={quoteNumber}
              options={quoteOptions}
              isLoading={isLoadingQuotes}
              showAIHint={showQuoteNumberAiHint}
              onChange={handleQuoteNumberChange}
            />
          </div>
          <div className='col-span-2'>
            <Label
              name='rateMin'
              hideAIHint
              className='text-sm text-neutral-800'
            >
              Rate Min
            </Label>
            <Input
              name='rateMin'
              className='mt-1'
              value={rateMin}
              placeholder='Optional'
              aria-label='Rate Min'
              onChange={(e) =>
                upsertRateRef(MGB_RATE_MIN_REF_TYPE, e.target.value)
              }
            />
          </div>
          <div className='col-span-2'>
            <Label
              name='rateMax'
              hideAIHint
              className='text-sm text-neutral-800'
            >
              Rate Max
            </Label>
            <Input
              name='rateMax'
              className='mt-1'
              value={rateMax}
              placeholder='Optional'
              aria-label='Rate Max'
              onChange={(e) =>
                upsertRateRef(MGB_RATE_MAX_REF_TYPE, e.target.value)
              }
            />
          </div>
        </>
      ) : (
        <>
          {quoteNumber && (
            <div className='col-span-4'>
              <Label
                name='quoteNumber'
                hideAIHint
                className='text-sm text-neutral-800'
              >
                Quote Number
              </Label>
              <Input
                name='quoteNumber'
                className='mt-1'
                value={quoteNumber}
                readOnly
                onChange={() => undefined}
                aria-label='Quote Number'
              />
            </div>
          )}
          {(rateMin || rateMax) && (
            <>
              {rateMin && (
                <div className='col-span-2'>
                  <Label
                    name='rateMin'
                    hideAIHint
                    className='text-sm text-neutral-800'
                  >
                    Rate Min
                  </Label>
                  <Input
                    name='rateMin'
                    className='mt-1'
                    value={rateMin}
                    readOnly
                    onChange={() => undefined}
                    aria-label='Rate Min'
                  />
                </div>
              )}
              {rateMax && (
                <div className='col-span-2'>
                  <Label
                    name='rateMax'
                    hideAIHint
                    className='text-sm text-neutral-800'
                  >
                    Rate Max
                  </Label>
                  <Input
                    name='rateMax'
                    className='mt-1'
                    value={rateMax}
                    readOnly
                    onChange={() => undefined}
                    aria-label='Rate Max'
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      <div className='col-span-4'>
        <Label
          name='customerRep'
          hideAIHint
          className='text-sm text-neutral-800'
        >
          Customer Rep
        </Label>
        <SpecialReferenceSelect
          value={customerRep}
          options={customerRepOptions}
          isLoading={isCustomerRepsLoading}
          customerSelected={customerSelected}
          ariaLabel='Customer Rep'
          placeholder='Select customer rep'
          onChange={(code) =>
            upsertSpecialReference(MGB_CUSTOMER_REP_REF_TYPE, code)
          }
        />
      </div>
      <div className='col-span-4'>
        <Label
          name='carrierRep'
          hideAIHint
          className='text-sm text-neutral-800'
        >
          Carrier Rep
        </Label>
        <SpecialReferenceSelect
          value={carrierRep}
          options={carrierRepOptions}
          isLoading={isCarrierRepsLoading}
          customerSelected={customerSelected}
          ariaLabel='Carrier Rep'
          placeholder='Select carrier rep'
          onChange={(code) =>
            upsertSpecialReference(MGB_CARRIER_REP_REF_TYPE, code)
          }
        />
      </div>
      <div className='col-span-4'>
        <Label
          name='trailerLength'
          hideAIHint
          className='text-sm text-neutral-800'
        >
          Trailer Length
        </Label>
        <SpecialReferenceSelect
          value={trailerLength}
          options={trailerLengthOptions}
          isLoading={isTrailerLengthsLoading}
          customerSelected={customerSelected}
          ariaLabel='Trailer Length'
          placeholder='Select trailer length'
          onChange={(code) =>
            upsertSpecialReference(MGB_TRAILER_LENGTH_REF_TYPE, code)
          }
        />
      </div>

      {showBol && (
        <div className='col-span-4'>
          <LoadBuildingTextInput
            name='customer.refNumber'
            label='BOL'
            readOnly
          />
        </div>
      )}

      {editableIndexes.map((index, displayIndex) => (
        <div
          key={fields[index].id}
          className='col-span-4 rounded border border-neutral-200 p-3'
        >
          <Flex align='center' justify='between' className='mb-3'>
            <Typography variant='body-sm' weight='semibold'>
              Reference #{displayIndex + 1}
            </Typography>
            <Button
              buttonNamePosthog={ButtonNamePosthog.MGBRemoveNote}
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => remove(index)}
              aria-label={`Remove reference ${displayIndex + 1}`}
            >
              <Trash2Icon className='h-4 w-4' />
            </Button>
          </Flex>

          <Flex direction='col' gap='lg' className='w-full'>
            <div className='w-full'>
              <Flex direction='col' className='w-full'>
                <Label
                  name={`additionalReferences.${index}.qualifier`}
                  required
                >
                  Type
                </Label>
                <Controller
                  name={`additionalReferences.${index}.qualifier`}
                  control={control}
                  rules={{
                    required: 'Reference type is required.',
                    validate: (value) =>
                      String(value ?? '').trim() !== '' ||
                      'Reference type is required.',
                  }}
                  render={({ field: qualifierField }) => (
                    <Select
                      value={qualifierField.value || undefined}
                      onValueChange={qualifierField.onChange}
                      disabled={
                        isLoading && selectableReferenceTypes.length === 0
                      }
                    >
                      <SelectTrigger
                        className='mt-1 w-full text-[13px]'
                        aria-label='Reference type'
                      >
                        <SelectValue placeholder='Select reference type' />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableReferenceTypes.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name || item.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name={`additionalReferences.${index}.qualifier`}
                  render={({ message }: { message: string }) => (
                    <Typography
                      variant='body-xs'
                      className='text-error-500 mt-1'
                    >
                      {message}
                    </Typography>
                  )}
                />
              </Flex>
            </div>

            <LoadBuildingTextInput
              name={`additionalReferences.${index}.number`}
              label='Value'
              required
              placeholder='Enter value'
            />
          </Flex>
        </div>
      ))}

      {/* empty:hidden drops the grid row when no candidate is left. */}
      <div className='col-span-4 empty:hidden'>
        <ReferenceCandidates
          candidates={referenceCandidates}
          adapter={referenceCandidateAdapter}
        />
      </div>

      <div className='col-span-4'>
        <Flex align='center' justify='end' className='mr-px mt-1'>
          <Button
            buttonNamePosthog={ButtonNamePosthog.MGBAddNote}
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => append(emptyAdditionalReference)}
            className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
          >
            + Add Reference
          </Button>
        </Flex>
      </div>
    </Grid>
  );
}
